/**
 * Live connection to a game's Durable Object.
 *
 * Replaces the Firebase original's `initGameListener()` / `onSnapshot` pair.
 * The original had to guess whether an inbound snapshot was an echo of its own
 * write and merged around it for 1.5 seconds (`pendingLocalWriteUntil`). Here
 * the server is the only writer, so every message is simply authoritative --
 * out-of-order frames are dropped by version number and nothing else is needed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientCommand, ServerMessage, ViewerRole } from '../../shared/protocol';
import type { GameState } from '../../shared/types';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface CelebrationEvent {
  id: number;
  reason: 'score' | 'assign' | 'twist';
}

export interface GameSocket {
  state: GameState | null;
  role: ViewerRole;
  status: ConnectionStatus;
  viewers: number;
  error: string | null;
  /** Latest celebration, for the confetti burst. Changes identity each time. */
  celebration: CelebrationEvent | null;
  send: (command: ClientCommand) => void;
  clearError: () => void;
}

const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export function useGameSocket(gameId: string | null): GameSocket {
  const [state, setState] = useState<GameState | null>(null);
  const [role, setRole] = useState<ViewerRole>('spectator');
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [viewers, setViewers] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [celebration, setCelebration] = useState<CelebrationEvent | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const versionRef = useRef(0);
  const closedRef = useRef(false);
  /** Commands issued while the socket was down, replayed on reconnect. */
  const queueRef = useRef<ClientCommand[]>([]);

  const send = useCallback((command: ClientCommand) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(command));
    } else {
      queueRef.current.push(command);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!gameId) return;
    closedRef.current = false;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (closedRef.current) return;
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(
        `${scheme}://${window.location.host}/ws/game/${encodeURIComponent(gameId)}`,
      );
      socketRef.current = ws;
      setStatus(attemptRef.current === 0 ? 'connecting' : 'reconnecting');

      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus('open');
        // Flush anything issued while offline. Commands are validated
        // server-side, so a stale one is rejected rather than misapplied.
        const queued = queueRef.current;
        queueRef.current = [];
        for (const cmd of queued) ws.send(JSON.stringify(cmd));
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' } satisfies ClientCommand));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(event.data as string) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case 'state':
            versionRef.current = msg.state.version;
            setState(msg.state);
            setRole(msg.role);
            break;
          case 'patch':
            // A reconnect can deliver a frame older than what we hold.
            if (msg.version < versionRef.current) return;
            versionRef.current = msg.version;
            setState(msg.state);
            break;
          case 'presence':
            setViewers(msg.viewers);
            break;
          case 'celebrate':
            setCelebration({ id: Date.now(), reason: msg.reason });
            break;
          case 'error':
            setError(msg.message);
            break;
          case 'pong':
            break;
        }
      };

      ws.onclose = () => {
        clearInterval(pingTimer);
        if (closedRef.current) return;
        setStatus('reconnecting');
        // Exponential backoff with jitter, so a DO restart does not bring every
        // viewer back in the same instant.
        const backoff = Math.min(1000 * 2 ** attemptRef.current, MAX_BACKOFF_MS);
        attemptRef.current += 1;
        retryTimer = setTimeout(connect, backoff * (0.5 + Math.random() * 0.5));
      };

      ws.onerror = () => ws.close();
    };

    connect();

    // Browsers freeze sockets on a backgrounded tab; reconnect on return so a
    // phone left locked through a quarter catches up immediately.
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        socketRef.current?.readyState !== WebSocket.OPEN
      ) {
        clearTimeout(retryTimer);
        attemptRef.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(pingTimer);
      clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
      setStatus('closed');
    };
  }, [gameId]);

  return { state, role, status, viewers, error, celebration, send, clearError };
}
